import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import type { InvoiceEntity } from "../../api/types";
import { invoicePath } from "../../logic/invoice-path";

export type InvoiceDuplicateDialogProps = {
  /** The invoice the server says the new one repeats. */
  existing: InvoiceEntity;
  /**
   * Offer to delete the attachment the form was opened from. Only for a
   * fresh upload nothing references yet — deleting a file another invoice or
   * transaction already links would take it away from them.
   */
  canDiscardUpload: boolean;
  /** A save or discard is in flight; the actions wait for it. */
  busy: boolean;
  /** Called once the existing invoice's page has been navigated to. */
  onOpenExisting: () => void;
  onDiscardUpload: () => void;
  onCreateAnyway: () => void;
  onCancel: () => void;
};

/**
 * Shown when `POST /lists/invoices` answers `invoiceDuplicate`: the vendor's
 * detector rule matched an invoice that already exists. The user decides —
 * look at the existing one, throw the upload away, or insist.
 */
export const InvoiceDuplicateDialog = ({
  existing,
  canDiscardUpload,
  busy,
  onOpenExisting,
  onDiscardUpload,
  onCreateAnyway,
  onCancel,
}: InvoiceDuplicateDialogProps) => {
  const navigate = useNavigate();

  const handleOpenExisting = () => {
    navigate(invoicePath(existing.id));
    onOpenExisting();
  };

  return (
    <Dialog open onClose={busy ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>This looks like an invoice you already have</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          The details match an existing invoice from this sender. If it is the
          same invoice, open the existing one instead of saving a second copy.
        </DialogContentText>
        <Stack spacing={0.5}>
          <Typography variant="subtitle1">{existing.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            Received {existing.dateReceived} · Due {existing.dueDate}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
        <Button onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        {canDiscardUpload && (
          <Button color="error" onClick={onDiscardUpload} disabled={busy}>
            Discard upload
          </Button>
        )}
        {/* The guard exists to stop duplicates, so the emphasised action is
            the one that keeps the existing invoice; creating another must be
            the choice you have to reach for. */}
        <Button onClick={onCreateAnyway} disabled={busy}>
          Create anyway
        </Button>
        <Button
          variant="contained"
          onClick={handleOpenExisting}
          disabled={busy}
        >
          Open existing
        </Button>
      </DialogActions>
    </Dialog>
  );
};
